<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { PointerDownOutsideEvent } from 'reka-ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button
} from '@/shared/components/ui'
import { cn } from '@/lib/utils'
import { actionLoadingText } from '@/shared/utils/actionLoadingText'

interface Props {
  title?: string
  description?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showFooter?: boolean
  submitText?: string
  cancelText?: string
  loading?: boolean
  submitVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'success'
  loadingText?: string
  testId?: string
  submitTestId?: string
  cancelTestId?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  showFooter: false,
  submitText: 'Save Changes',
  cancelText: 'Cancel',
  loading: false,
  submitVariant: 'default',
  disabled: false
})

const resolvedLoadingText = computed(() => props.loadingText ?? actionLoadingText(props.submitText))

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'cancel'): void
}>()

const isOpen = defineModel<boolean>('isOpen', { default: false })

const route = useRoute()
if (route) {
  watch(
    () => route.fullPath,
    () => {
      if (isOpen.value) {
        isOpen.value = false
      }
    }
  )
}

const sizeClasses = {
  sm: 'sm:max-w-[425px]',
  md: 'sm:max-w-[600px]',
  lg: 'sm:max-w-[800px]',
  xl: 'sm:max-w-[1000px]',
  full: 'sm:max-w-[95vw] h-[95vh]'
}

const handleCancel = () => {
  isOpen.value = false
  emit('cancel')
}

// Modal dialogs block body pointer events; teleported select dropdowns live on body.
const isSelectDropdownTarget = (target: EventTarget | null) =>
  (target as HTMLElement | null)?.closest('.app-select-dropdown, .multiselect__content-wrapper')

const onPointerDownOutside = (event: PointerDownOutsideEvent) => {
  if (isSelectDropdownTarget(event.detail.originalEvent.target)) {
    event.preventDefault()
  }
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="(val) => (isOpen = val)">
    <DialogContent
      :data-testid="testId"
      overlay-class="backdrop-blur-sm"
      @open-auto-focus="(e) => e.preventDefault()"
      @pointer-down-outside="onPointerDownOutside"
      :class="
        cn(
          sizeClasses[size],
          'flex flex-col p-0 gap-0 outline-none max-h-[90vh] overflow-hidden',
          size === 'full' && 'rounded-none'
        )
      "
    >
      <!-- Header -->
      <slot name="header">
        <DialogHeader
          v-if="title || description"
          class="shrink-0 px-6 py-5 border-b bg-card gap-y-1"
        >
          <DialogTitle
            v-if="title"
            class="text-xl font-bold leading-none tracking-tight text-foreground"
          >
            {{ title }}
          </DialogTitle>
          <DialogDescription
            v-if="description"
            class="text-sm text-muted-foreground mt-0 leading-normal"
          >
            {{ description }}
          </DialogDescription>
        </DialogHeader>
      </slot>

      <!-- Body / Content -->
      <div
        :class="
          cn(
            'flex-1 min-h-0 overflow-y-auto overscroll-contain p-6 app-scrollbar font-normal text-foreground/90',
            !showFooter && 'pb-8'
          )
        "
      >
        <slot />
      </div>

      <!-- Footer -->
      <slot name="footer">
        <DialogFooter
          v-if="showFooter"
          class="shrink-0 px-6 py-4 bg-muted/30 border-t flex flex-row items-center justify-end gap-3"
        >
          <Button
            variant="ghost"
            @click="handleCancel"
            :disabled="loading"
            :data-testid="cancelTestId || (testId ? `${testId}-cancel-button` : undefined)"
            class="font-semibold px-4"
          >
            {{ cancelText }}
          </Button>
          <Button
            :variant="submitVariant"
            @click="emit('submit')"
            :loading="loading"
            :loading-text="resolvedLoadingText"
            :disabled="disabled"
            :data-testid="submitTestId || (testId ? `${testId}-submit-button` : undefined)"
            class="font-semibold px-6 shadow-sm"
          >
            {{ submitText }}
          </Button>
        </DialogFooter>
      </slot>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
.app-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: hsl(var(--muted-foreground) / 0.3) transparent;
}
</style>
